/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 * DatePicker header hook - only for separate header
 * - change selectors (month & year) handler
 * - get selectors (month & year) options
 *
 * @author Yakov Gushchin
 * @version 1.10.0
 * @since 1.10.0
 */

import { useCallback, useMemo } from 'react';

import moment, { Moment, unitOfTime } from 'moment';

import { IOptionItemProps } from '../../../Select/Select';
import { IDatePickerHeaderProps } from '../../types/datePickerTypes';


interface IUseDatePickerHeaderSeparate {
    locale: IDatePickerHeaderProps['locale'];
    maxDate: IDatePickerHeaderProps['maxDate'];
    minDate: IDatePickerHeaderProps['minDate'];
    period: IDatePickerHeaderProps['period'];
    periodChangeHandler: IDatePickerHeaderProps['periodChangeHandler'];
}

const useDatePickerHeaderSeparate = (props: IUseDatePickerHeaderSeparate) => {

    const {
        locale,
        maxDate,
        minDate,
        period,
        periodChangeHandler,
    } = props;

    const selectorChangeHandler = useCallback((selectDuration: unitOfTime.Base, select: IOptionItemProps): void => {
        periodChangeHandler(
            selectDuration === 'month'
                ? moment(new Date(period.year(), +select.value))
                : moment(new Date(+select.value, period.month())),
        );
    }, [periodChangeHandler, period]);

    const selectorOptions: { [key: string]: IOptionItemProps[] } = useMemo(() => ({
        'month': locale.monthNames
            .map((month: string, key: number) => ({ label: month, value: key }))
            .filter((month: IOptionItemProps) =>
                !minDate || moment(new Date(period.year(), +month.value)).unix() > minDate.unix()),
        'year': getYearNames(minDate, maxDate)
            .filter((year: number) => !minDate || moment(new Date(year, period.month())).unix() >= minDate.unix())
            .map((year: number) => ({ label: year.toString(), value: year })),
    }), [locale, minDate, maxDate, period]);

    const selectedOption: { [key: string]: IOptionItemProps } = useMemo(() => ({
        'month': { label: locale.monthNames[period.month()], value: period.month() },
        'year': { label: period.year().toString(), value: period.year() },
    }), [period, locale]);

    return {
        selectedOption,
        selectorChangeHandler,
        selectorOptions,
    };
};

const calendar = moment();
const getDefaultMinYear = (min: Moment): string => {
    return min.format('YYYY');
};

const getDefaultMaxYear = (max: Moment): string => {
    return max.format('YYYY');
};

const getYearNames = (min?: Moment, max?: Moment): number[] => {
    const sY = +getDefaultMinYear(min?.clone() || calendar.clone().subtract(5, 'year'));
    let eY = +getDefaultMaxYear(max?.clone() || calendar.clone().add(6, 'year'));
    const yearNames = [];
    for (; eY >= sY; eY--) {
        yearNames.push(eY);
    }
    return yearNames;
};

export default useDatePickerHeaderSeparate;
